// Shared helpers for the layer-card schema/view tree used by both the
// Data Access Groups editor and the per-user Data Access modal.
//
// Three categories: RAW / REFINED / ANALYTIC. Items not matching RAW_*
// or ANALYTIC_* prefixes default to REFINED so admins can still target
// them.

import { getCachedDatabaseCatalog, getCatalogDatabase } from '../services/dataService';

export const LAYER_KEYS = ['RAW', 'REFINED', 'ANALYTIC'];

export function categorize(schemaName) {
  const up = (schemaName || '').toUpperCase();
  if (up.startsWith('RAW')) return 'RAW';
  if (up.startsWith('ANALYTIC')) return 'ANALYTIC';
  return 'REFINED';
}

export const schemaKey = (s) => `S:${s.toUpperCase()}`;
export const viewKey = (s, v) => `V:${s.toUpperCase()}.${v.toUpperCase()}`;

/**
 * Normalise a catalog payload's schema list into [{schema, views:[name]}].
 */
export function normalizeSchemaList(catalog) {
  return (catalog?.schemas || [])
    .map((s) => {
      const rawViews = s.VIEWS ?? s.views ?? [];
      let viewsArr;
      if (Array.isArray(rawViews)) {
        viewsArr = rawViews;
      } else if (typeof rawViews === 'string') {
        viewsArr = rawViews.split(',').map((v) => v.trim()).filter(Boolean);
      } else {
        viewsArr = [];
      }
      return {
        schema: s.SCHEMA || s.name || '',
        views: viewsArr
          .map((v) => (typeof v === 'string' ? v : v.NAME || v.name || v.VIEW_NAME || ''))
          .filter(Boolean),
      };
    })
    .filter((s) => s.schema);
}

/**
 * Load the full schema universe across RAW/REFINED/ANALYTIC layers,
 * filling any layer that the cached catalog is missing via a live scan.
 *
 * Returns { schemas: [{schema, views}], viewsBySchemaUpper, allKeys }.
 */
export async function loadSchemaUniverse() {
  const catalog = await getCachedDatabaseCatalog();
  let schList = normalizeSchemaList(catalog);

  const presentCategories = new Set(schList.map((s) => categorize(s.schema)));
  const layerPrefixMap = { RAW: 'RAW_', REFINED: 'REFINED_', ANALYTIC: 'ANALYTIC_' };
  const missingLayers = Object.keys(layerPrefixMap).filter((c) => !presentCategories.has(c));

  if (missingLayers.length > 0) {
    const results = await Promise.allSettled(
      missingLayers.map((c) => getCatalogDatabase(layerPrefixMap[c])),
    );
    const seen = new Set(schList.map((s) => s.schema.toUpperCase()));
    for (const r of results) {
      if (r.status !== 'fulfilled') continue;
      for (const item of normalizeSchemaList(r.value)) {
        const key = item.schema.toUpperCase();
        if (seen.has(key)) continue;
        seen.add(key);
        schList.push(item);
      }
    }
  }

  const viewsBySchemaUpper = {};
  const allKeys = [];
  for (const s of schList) {
    const sUpper = s.schema.toUpperCase();
    viewsBySchemaUpper[sUpper] = s.views;
    allKeys.push(schemaKey(s.schema));
    for (const v of s.views) allKeys.push(viewKey(s.schema, v));
  }

  return { schemas: schList, viewsBySchemaUpper, allKeys };
}

/**
 * Given a list of denial entries [{schema_name, table_name}], return the
 * set of tree keys (schema+view) that are denied.
 */
export function denialsToKeySet(denials, viewsBySchemaUpper) {
  const out = new Set();
  for (const d of denials || []) {
    const s = (d.schema_name || '').toUpperCase();
    const t = (d.table_name || '').toUpperCase();
    if (!s) continue;
    if (!t) {
      out.add(schemaKey(s));
      for (const v of (viewsBySchemaUpper[s] || [])) out.add(viewKey(s, v));
    } else {
      out.add(viewKey(s, t));
    }
  }
  return out;
}

/**
 * Given a set of "checked = denied" keys, return a normalised denial list
 * (whole-schema rows collapsed when every view of the schema is denied).
 */
export function keysToDenials(checkedKeys, schemas, viewsBySchemaUpper) {
  const checkedSet = new Set(checkedKeys);
  const denials = [];
  for (const s of schemas) {
    const sUpper = s.schema.toUpperCase();
    const views = viewsBySchemaUpper[sUpper] || [];
    const schemaChecked = checkedSet.has(schemaKey(s.schema));
    const deniedViews = views.filter((v) => checkedSet.has(viewKey(s.schema, v)));
    if (schemaChecked && (views.length === 0 || deniedViews.length === views.length)) {
      // Whole schema denied (or schema-only checkbox with no views known).
      denials.push({ schema_name: s.schema, table_name: '' });
    } else {
      for (const v of deniedViews) denials.push({ schema_name: s.schema, table_name: v });
    }
  }
  return denials;
}
